import { Button, SearchBar, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { CompanySortDirection } from "../../../api/masterCompanies.service";
import {
  pendingCompaniesService,
  type PendingCompany,
  type PendingCompanySortField,
  type ReviewStatus,
} from "../../../api/pendingCompanies.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import { PageHeader } from "../../../components/PageHeader";
import EmptyState from "../../../components/EmptyState";
import LoadingState from "../../../components/LoadingState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { usePermissions } from "../../../hooks/usePermissions";
import { formatDateTime } from "../../../utils";
import ReviewPanel from "./ReviewPanel";
import { ResolvedCompany } from "./ResolvedCompany";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import reviewStyles from "./Review.module.css";
import styles from "../MasterCompanies/MasterCompanies.module.css";

const PAGE_SIZE = 25;
const SORT_FIELDS = ["name", "createdAt", "status", "userId", "resolvedCompany"] as const;
const SORT_DIRECTIONS = ["asc", "desc"] as const;
const SORTABLE_COLUMNS: string[] = [...SORT_FIELDS];
const SORT_FIELD_BY_ACCESSOR: Partial<Record<string, string>> = { companyId: "resolvedCompany" };
const readSortField = (value: string | null): PendingCompanySortField =>
  SORT_FIELDS.find(field => field === value) ?? "createdAt";
const readSortDirection = (value: string | null): CompanySortDirection =>
  SORT_DIRECTIONS.find(candidate => candidate === value) ?? "asc";
const COLUMNS: Column<PendingCompany>[] = [
  {
    header: "Name",
    accessor: "name",
    wrap: true,
  },
  {
    header: "Resolved company",
    accessor: "companyId",
    wrap: true,
    render: (_value, row) => <ResolvedCompany record={row} />,
  },
  {
    header: "User ID",
    accessor: "userId",
    render: (_value, row) => <span className={styles.mono}>{row.userId}</span>,
  },
  {
    header: "Submitted",
    accessor: "createdAt",
    render: (_value, row) => formatDateTime(row.createdAt),
  },
  {
    header: "Status",
    accessor: "status",
    render: (_value, row) => <ReviewStatusBadge status={row.status} />,
  },
];

export default function PendingCompaniesPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.website_companies.read");
  const canWrite = hasPermission("staff.website_companies.write");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const status = (["all", "pending", "approved", "rejected"].find(
    value => value === params.get("status"),
  ) ?? "pending") as ReviewStatus | "all";
  const committedSearch = params.get("search") ?? "";
  const parsedPage = Number(params.get("page"));
  const page =
    Number.isSafeInteger(parsedPage) && parsedPage > 0
      ? Math.min(parsedPage, Math.floor(2147483647 / PAGE_SIZE) + 1)
      : 1;
  const sort = readSortField(params.get("sort"));
  const direction = readSortDirection(params.get("direction"));
  const [search, setSearch] = useState(committedSearch);
  const [data, setData] = useState<PendingCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const results = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(committedSearch);
  }, [committedSearch]);
  useEffect(() => {
    if (search === committedSearch) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      search ? next.set("search", search) : next.delete("search");
      next.delete("page");
      setParams(next);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, committedSearch, params, setParams]);
  useEffect(() => {
    if (!canRead) return;
    let active = true;
    setLoading(true);
    setError(null);
    pendingCompaniesService
      .list(committedSearch, PAGE_SIZE, (page - 1) * PAGE_SIZE, sort, direction, status)
      .then(result => {
        if (!active) return;
        setData(result.data);
        setTotal(result.meta.total);
      })
      .catch(caught => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Failed to load pending companies.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canRead, committedSearch, page, sort, direction, status, revision]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  useEffect(() => {
    if (loading || total === 0 || page <= lastPage) return;
    const next = new URLSearchParams(params);
    lastPage > 1 ? next.set("page", String(lastPage)) : next.delete("page");
    setParams(next, { replace: true });
  }, [loading, total, page, lastPage, params, setParams]);

  const handleSort = (field: string) => {
    const next = new URLSearchParams(params);
    next.set("sort", field);
    next.set("direction", field === sort && direction === "asc" ? "desc" : "asc");
    next.delete("page");
    setParams(next);
  };

  if (!canRead) return <UnauthorizedView />;
  return (
    <div className={styles.wrapper}>
      <PageHeader title="Pending companies" subtitle="Companies entered by website users" />
      <div className={styles.listContent}>
        <div className={reviewStyles.filters}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search by name..." />
          <label className={`${reviewStyles.field} ${reviewStyles.statusField}`}>
            Status
            <select
              value={status}
              onChange={event => {
                const next = new URLSearchParams(params);
                next.set("status", event.target.value);
                next.delete("page");
                setParams(next);
              }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
        </div>
        {error ? (
          <div>
            <EmptyState variant="error" title="Failed to load pending companies" message={error} />
            <Button
              color="primary"
              className={reviewStyles.brandButton}
              onClick={() => setRevision(value => value + 1)}
            >
              Retry
            </Button>
          </div>
        ) : loading && data.length === 0 ? (
          <LoadingState message="Loading pending companies..." />
        ) : (
          <div ref={results} tabIndex={-1} className={styles.results} aria-busy={loading}>
            <DataTable
              data={data}
              columns={COLUMNS}
              onRowClick={row => setSelectedId(row.id)}
              rowLabel={row => `View details for ${row.name}`}
              rowKey="id"
              emptyMessage="No pending companies found."
              sortableColumnKeys={SORTABLE_COLUMNS}
              sortFieldByAccessor={SORT_FIELD_BY_ACCESSOR}
              sortField={sort}
              sortDirection={direction}
              onSort={handleSort}
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={lastPage}
              startIndex={(currentPage - 1) * PAGE_SIZE}
              endIndex={Math.min(currentPage * PAGE_SIZE, total)}
              totalCount={total}
              itemName="companies"
              onPageChange={nextPage => {
                const next = new URLSearchParams(params);
                nextPage > 1 ? next.set("page", String(nextPage)) : next.delete("page");
                setParams(next);
              }}
            />
          </div>
        )}
      </div>
      {selectedId && (
        <ReviewPanel
          id={selectedId}
          canWrite={canWrite}
          onClose={() => {
            setSelectedId(null);
            requestAnimationFrame(() => {
              if (document.activeElement === document.body) results.current?.focus();
            });
          }}
          onRefresh={() => setRevision(value => value + 1)}
        />
      )}
    </div>
  );
}
